import { RESOURCE_TYPE } from '@models'
import {
  HIRE_SEARCH_CONTENT_TYPE_SLUGS,
  INDUSTRY_SEARCH_CONTENT_TYPE_SLUGS,
  SERVICE_SEARCH_CONTENT_TYPE_SLUGS,
  typesenseContentTypeFilter,
} from '@utils/search/page-content-types'

export const SITE_SEARCH_CATEGORY = {
  All: 'all',
  Services: 'services',
  Industries: 'industries',
  Hire: 'hire',
  Blogs: 'blogs',
  CaseStudies: 'case-studies',
} as const

export type SiteSearchCategory =
  (typeof SITE_SEARCH_CATEGORY)[keyof typeof SITE_SEARCH_CATEGORY]

export const SITE_SEARCH_CONTENT_CATEGORIES = [
  SITE_SEARCH_CATEGORY.Services,
  SITE_SEARCH_CATEGORY.Industries,
  SITE_SEARCH_CATEGORY.Hire,
  SITE_SEARCH_CATEGORY.Blogs,
  SITE_SEARCH_CATEGORY.CaseStudies,
] as const

export type SiteSearchContentCategory =
  (typeof SITE_SEARCH_CONTENT_CATEGORIES)[number]

export const SITE_SEARCH_CATEGORIES = Object.values(
  SITE_SEARCH_CATEGORY
) as SiteSearchCategory[]

/**
 * Page-collection filters keyed by PageConfiguration contentType.slug.
 * Resources (blogs / case studies) are separate collections.
 */
export const PAGE_SEARCH_CATEGORY_FILTER: Partial<
  Record<SiteSearchContentCategory, string>
> = {
  [SITE_SEARCH_CATEGORY.Services]: typesenseContentTypeFilter(
    SERVICE_SEARCH_CONTENT_TYPE_SLUGS
  ),
  [SITE_SEARCH_CATEGORY.Industries]: typesenseContentTypeFilter(
    INDUSTRY_SEARCH_CONTENT_TYPE_SLUGS
  ),
  [SITE_SEARCH_CATEGORY.Hire]: typesenseContentTypeFilter(
    HIRE_SEARCH_CONTENT_TYPE_SLUGS
  ),
}

export const CATEGORY_TO_RESOURCE_TYPE: Partial<
  Record<SiteSearchCategory, RESOURCE_TYPE>
> = {
  [SITE_SEARCH_CATEGORY.Blogs]: RESOURCE_TYPE.Blogs,
  [SITE_SEARCH_CATEGORY.CaseStudies]: RESOURCE_TYPE.CaseStudies,
}

export type SiteSearchPayload = {
  q: string
  count?: number
  /** Defaults to all grouped collections. */
  category?: SiteSearchCategory
}

export type SiteSearchHitDocument = {
  id: string
  slug: string
  title: string
  shortDescription?: string
  content?: string
  pageType?: string
  sitemapCategory?: string
  contentTypeSlug?: string
  imageUrl?: string
  href?: string
  resourcesType?: string
  categories?: string[]
  authorName?: string
  authorSlug?: string
  authorImageAltTag?: string
  authorImageUrl?: string
  readingTime?: string
  imageAltTag?: string
  modifiedAt?: string
  createdAt?: string
  logoUrl?: string
  logoAltTag?: string
  caseStudyStartDate?: string
  caseStudyEndDate?: string
}

export type TypesenseHit = {
  document: SiteSearchHitDocument
  text_match?: number
}

export type TypesenseResult = {
  found?: number
  hits?: TypesenseHit[]
  request_params?: { collection_name?: string }
}

export type SiteSearchGroupedResults = Partial<
  Record<SiteSearchContentCategory, TypesenseResult>
>

export type SiteSearchResponse = {
  category: SiteSearchCategory
  results: TypesenseResult[]
  groups?: SiteSearchGroupedResults
}

export function isSiteSearchCategory(
  value: unknown
): value is SiteSearchCategory {
  return (
    typeof value === 'string' &&
    SITE_SEARCH_CATEGORIES.includes(value as SiteSearchCategory)
  )
}

export function isPageSearchCategory(
  category: SiteSearchContentCategory
): boolean {
  return category in PAGE_SEARCH_CATEGORY_FILTER
}
